import { useRef, useState } from 'react';
import {
    autoScrollVelocity,
    fillTargetRange,
    rowIndexAtPoint,
} from './fillDragGeometry';

// The pointer must travel this far (px, vertically) before a press on the
// handle starts filling, so a click or double-click with a little jitter
// never fills a row.
const DRAG_THRESHOLD = 4;
// autoScrollVelocity is in px per 60 Hz frame. Scrolling by elapsed time keeps
// the speed the same on faster displays; after a stalled frame the scroll
// catches up by at most this many frames.
const MAX_CATCH_UP_FRAMES = 4;

function sameRange(left, right) {
    if (left === right) return true;
    return Boolean(left && right)
        && left.startIndex === right.startIndex
        && left.endIndex === right.endIndex;
}

function normalizeRange(range) {
    return {
        startIndex: Math.min(range.startIndex, range.endIndex),
        endIndex: Math.max(range.startIndex, range.endIndex),
    };
}

// The scroll element's visible box in viewport pixels, without a horizontal
// scrollbar.
function scrollerBounds(element) {
    const rect = element.getBoundingClientRect();
    const scrollerTop = rect.top + (element.clientTop || 0);
    return { scrollerTop, scrollerBottom: scrollerTop + (element.clientHeight || rect.height) };
}

// One controller per hook instance. The live session sits in this closure, so
// pointer, keyboard, scroll and animation-frame handlers share it, and every
// handler reads the hook's latest options through `getOptions`.
function createFillDragController(getOptions, setDrag) {
    let session = null;

    function updateTarget(bounds) {
        const { headerHeight, rowHeight, rowCount, onPreview } = getOptions();
        const hoverIndex = rowIndexAtPoint({
            clientY: session.clientY,
            ...bounds,
            scrollTop: session.scrollElement.scrollTop,
            headerHeight,
            rowHeight,
            rowCount,
        });
        const target = fillTargetRange(session.sourceRange, hoverIndex);
        if (sameRange(target, session.target)) return;
        session.target = target;
        setDrag({ targetRange: target });
        onPreview?.(target, session.sourceRange);
    }

    function scrollVelocity(bounds) {
        const { headerHeight, autoScrollEdge, autoScrollMaxSpeed } = getOptions();
        return autoScrollVelocity({
            clientY: session.clientY,
            ...bounds,
            headerHeight,
            edge: autoScrollEdge,
            maxSpeed: autoScrollMaxSpeed,
            originY: session.originY,
        });
    }

    function autoScrollFrame(time) {
        if (!session) return;
        session.frameId = null;
        const bounds = scrollerBounds(session.scrollElement);
        const velocity = scrollVelocity(bounds);
        if (velocity === 0) return;
        const frames = session.frameTime === null
            ? 1
            : Math.min(MAX_CATCH_UP_FRAMES, Math.max(0, ((time - session.frameTime) * 60) / 1000));
        session.frameTime = time;
        // Whole pixels only: a browser may round a fractional scrollTop away.
        session.scrollRemainder += velocity * frames;
        const step = Math.trunc(session.scrollRemainder);
        session.scrollRemainder -= step;
        if (step !== 0) session.scrollElement.scrollTop += step;
        // The rows under a still pointer change as the grid scrolls.
        updateTarget(bounds);
        session.frameId = requestAnimationFrame(autoScrollFrame);
    }

    function startAutoScroll(bounds) {
        if (session.frameId !== null || scrollVelocity(bounds) === 0) return;
        session.frameTime = null;
        session.scrollRemainder = 0;
        session.frameId = requestAnimationFrame(autoScrollFrame);
    }

    // Stops the session and clears any preview; returns the ended session.
    function end() {
        const ended = session;
        session = null;
        if (ended.frameId !== null) cancelAnimationFrame(ended.frameId);
        window.removeEventListener('keydown', ended.onKeyDown, true);
        ended.scrollElement.removeEventListener('scroll', ended.onScroll);
        if (ended.handle.hasPointerCapture?.(ended.pointerId)) {
            ended.handle.releasePointerCapture(ended.pointerId);
        }
        setDrag(null);
        if (ended.target) getOptions().onPreview?.(null, ended.sourceRange);
        return ended;
    }

    function cancel() {
        if (!session) return;
        end();
        getOptions().onCancel?.();
    }

    const isSessionPointer = (event) => session !== null && event.pointerId === session.pointerId;

    return {
        // React calls this with null when the handle unmounts, e.g. because
        // the selection changed mid-drag: drop the session with its listeners.
        ref(element) {
            if (element === null) cancel();
        },
        onPointerDown(event) {
            if (event.button !== 0 || event.isPrimary === false) return;
            if (session) {
                // Another pointer is ignored. The same pointer pressing again
                // means the handle never saw its release: start over.
                if (event.pointerId !== session.pointerId) return;
                cancel();
            }
            const { getScrollElement, sourceRange } = getOptions();
            const scrollElement = getScrollElement?.();
            if (!scrollElement || !sourceRange) return;
            // A press on the handle only ever fills: no focus change, text
            // selection or grid range selection underneath it.
            event.preventDefault();
            event.stopPropagation();
            const handle = event.currentTarget;
            handle.setPointerCapture?.(event.pointerId);
            const started = {
                pointerId: event.pointerId,
                handle,
                scrollElement,
                sourceRange: normalizeRange(sourceRange),
                originY: event.clientY,
                clientY: event.clientY,
                moved: false,
                target: null,
                frameId: null,
                frameTime: null,
                scrollRemainder: 0,
                onKeyDown(keyEvent) {
                    if (keyEvent.key !== 'Escape') return;
                    // Escape cancels the fill only; the grid keeps its selection.
                    keyEvent.preventDefault();
                    keyEvent.stopPropagation();
                    cancel();
                },
                // Wheel scrolling mid-drag moves other rows under a still pointer.
                onScroll() {
                    if (session === started && started.moved) updateTarget(scrollerBounds(scrollElement));
                },
            };
            session = started;
            window.addEventListener('keydown', started.onKeyDown, true);
            scrollElement.addEventListener('scroll', started.onScroll, { passive: true });
            setDrag({ targetRange: null });
        },
        onPointerMove(event) {
            if (!isSessionPointer(event)) return;
            session.clientY = event.clientY;
            if (!session.moved) {
                if (Math.abs(event.clientY - session.originY) < DRAG_THRESHOLD) return;
                session.moved = true;
            }
            const bounds = scrollerBounds(session.scrollElement);
            updateTarget(bounds);
            startAutoScroll(bounds);
        },
        onPointerUp(event) {
            if (!isSessionPointer(event)) return;
            const { target, sourceRange } = end();
            const { onCommit, onCancel } = getOptions();
            if (target) {
                onCommit?.(target, sourceRange);
            } else {
                onCancel?.();
            }
        },
        onPointerCancel(event) {
            if (isSessionPointer(event)) cancel();
        },
        onLostPointerCapture(event) {
            if (isSessionPointer(event)) cancel();
        },
        onDoubleClick(event) {
            event.preventDefault();
            event.stopPropagation();
            const { sourceRange, onFillToEnd } = getOptions();
            if (!session && sourceRange) onFillToEnd?.(normalizeRange(sourceRange));
        },
    };
}

/**
 * Drives one fill-handle drag at a time for a virtualized grid with a sticky
 * header. Grid-agnostic: it reports display-row ranges and never writes
 * values. Spread `handleProps` onto the handle element (FillHandle does).
 *
 * Options (read afresh on every event):
 * - getScrollElement(): the grid's scroll element.
 * - headerHeight: content y of row 0's top edge, i.e. the sticky header's
 *   rendered height including any bottom border.
 * - rowHeight, rowCount: fixed row height (px) and number of display rows.
 * - sourceRange: { startIndex, endIndex } display rows of the selection; it is
 *   fixed for a drag when the drag starts.
 * - autoScrollEdge, autoScrollMaxSpeed: optional, see autoScrollVelocity.
 * - onPreview(targetRange | null, sourceRange): the target changed, including
 *   back to null when the pointer returns into the source or a previewed drag
 *   ends. Drive ghost previews from it.
 * - onCommit(targetRange, sourceRange): released over a target.
 * - onCancel(): ended without a fill (Escape, pointercancel, lost capture,
 *   handle unmounted, released inside the source, or a new press after a
 *   release the handle never saw).
 * - onFillToEnd(sourceRange): the handle was double-clicked.
 *
 * Each drag ends with exactly one onCommit or onCancel. targetRange is
 * { startIndex, endIndex, direction: 'down' | 'up' }, inclusive and excluding
 * the source rows, the shape planFill takes.
 *
 * Returns { handleProps, isDragging, targetRange }.
 */
export default function useFillDrag(options) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const [drag, setDrag] = useState(null);
    const [handleProps] = useState(
        () => createFillDragController(() => optionsRef.current, setDrag),
    );
    return {
        handleProps,
        isDragging: drag !== null,
        targetRange: drag === null ? null : drag.targetRange,
    };
}
